import React, { memo, useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { showNotification } from '../../redux/notification';
import guaranteedIcon from '../../assets/images/guaranteed-icon.svg';
import { ReactComponent as Orientation } from '../../assets/images/badge-orientation.svg';
import xhr from '../../utils/xhr';
import utils from '../../utils/utils';
import LoadingSpinner from '../../components/spinner/LoadingSpinner';
import './ShiftsConfirmation.scss';

const ShiftsConfirmation = (props) => {
	const dispatch = useDispatch();
	const [hasGuaranteedSlot, setHasGuaranteedSlot] = useState(false);
	const [similarShifts, setSimilarShifts] = useState([]);
	const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
	const getOpenSlotsByShift = (slots) => slots.filter((slot) => !slot.caregiver_id);

	const getSameDayAndStructureShifts = (shiftDate, shiftStructure) => {
		const openShifts = [];
		similarShifts.forEach((day) => {
			const dayDate = moment(day.raw_calendar_date).format('MMMM DD');
			if (dayDate === shiftDate) {
				day.shifts.forEach((shift) => {
					if (shift.shift_structure_id === shiftStructure.id && !shift.isRecurring) {
						openShifts.push(shift);
					}
				});
			}
		});

		return openShifts;
	};

	const getOpenShiftsSlotsBeforePosting = (shiftDate, shiftStructure) => {
		const openShiftSlots = [];
		const sameShifts = getSameDayAndStructureShifts(shiftDate, shiftStructure);

		for (let i = 0; i < sameShifts.length; i++) {
			const shift = sameShifts[i];
			openShiftSlots.push(...getOpenSlotsByShift(shift.slots));
		}

		return openShiftSlots;
	};

	const checkAnySlotIsGuaranteed = () => {
		let isGuaranteed = false;
		props.shiftsGroups.forEach((group) => {
			const _hasGuaranteedSlot = group.shifts.some((shift) => !!shift.is_guaranteed);
			if (_hasGuaranteedSlot) {
				isGuaranteed = true;
			}
		});
		return isGuaranteed;
	};

	useEffect(() => {
		setHasGuaranteedSlot(checkAnySlotIsGuaranteed());
	}, [props.shiftGroups]); // eslint-disable-line react-hooks/exhaustive-deps

	const getSimilarShifts = useCallback(async () => {
		setShowLoadingSpinner(true);
		const shiftStructuresByDateParams = new URLSearchParams();

		props.shiftsGroups.map((group) =>
			group.shifts.map((shift) =>
				shiftStructuresByDateParams.append(moment(group.date).format('YYYY-MM-DD'), shift.shiftStructureId)
			)
		);

		try {
			const response = await xhr.request(
				'GET',
				`/facility/${props.facilityId}/shifts/similar?${shiftStructuresByDateParams.toString()}`
			);
			setSimilarShifts(response.data);
			setShowLoadingSpinner(false);
		} catch (e) {
			const errorMessage = utils.handleNetworkErrorMessage(e, 'Failed getting similar shifts. Please try again.');
			dispatch(showNotification('error', errorMessage));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, props.shiftsGroups]);

	useEffect(() => {
		getSimilarShifts();
	}, []);

	return showLoadingSpinner ? (
		<LoadingSpinner />
	) : (
		<table className="table shifts-confirmation-table">
			<thead className="header-confirmation-shifts d-none d-sm-flex">
				<tr className="header-confirmation-shifts-row">
					<th className="head-text resource-type">Shift Type</th>
					<th className="head-text">Date</th>
					<th className="head-text start-end">Start - End</th>
					{props.showUnits && <th className="head-text wrap">Unit</th>}
					<th className="head-text wrap">Shifts Requested</th>
					<th className="head-text wrap">Open Shifts Before Posting</th>
					<th className="head-text wrap">Total Open Shifts After Posting</th>
				</tr>
			</thead>
			<tbody>
				{props.shiftsGroups.map((shiftsGroup, groupIndex) =>
					shiftsGroup.shifts.map((shift, shiftIndex) => {
						const shiftStructure = props.shiftStructures.find((structure) => structure.id === shift.shiftStructureId);
						const end = moment.utc(shiftStructure.to_time, 'HH:mm:ss');
						const start = moment.utc(shiftStructure.from_time, 'HH:mm:ss');

						if (end.isBefore(start)) {
							end.add(1, 'days');
						}

						const monthAndDay = moment(shiftsGroup.date).format('MM/DD');
						const dateToCheck = moment(shiftsGroup.date).format('MMMM DD');
						const openShiftsSlotsBeforePosting = getOpenShiftsSlotsBeforePosting(dateToCheck, shiftStructure);
						const [prevShiftsGuaranteed, prevShiftsNotGuaranteed] = openShiftsSlotsBeforePosting.reduce(
							([guaranteed, notGuaranteed], slot) => {
								if (slot.is_guaranteed) {
									return [guaranteed + 1, notGuaranteed];
								}
								return [guaranteed, notGuaranteed + 1];
							},
							[0, 0]
						);

						const [newShiftsGuaranteed, newShiftsNotGuaranteed] = shift.isGuaranteed
							? [shift.numberOfProviders, 0]
							: [0, shift.numberOfProviders];

						const totalOpenGuaranteed = prevShiftsGuaranteed + newShiftsGuaranteed;
						const totalOpenNotGuaranteed = prevShiftsNotGuaranteed + newShiftsNotGuaranteed;

						let { unitName } = shift;
						const limit = 34;
						if (shift.unitName?.length > limit) {
							unitName = `${shift.unitName.substring(0, limit)}...`;
						}

						return (
							<tr
								// eslint-disable-next-line react/no-array-index-key
								key={`confirmation-details-${groupIndex}-${shiftIndex}`}
								className={classNames('row-confirmation-shifts request d-block d-sm-flex', {
									'first-row': shiftIndex === 0
								})}
							>
								<td className="row-shift">{shiftStructure.resource_type}</td>
								<td className="row-shift">
									{monthAndDay} - {moment(shiftsGroup.date).format('ddd')}
								</td>
								<td className="row-shift">
									{start.format('h:mm A')} - {end.format('h:mm A')}
								</td>
								{props.showUnits && (
									<OverlayTrigger
										placement="top"
										overlay={
											<Tooltip delay={{ show: 150, hide: 400 }} id="unit-tooltip">
												{shift.unitName}
											</Tooltip>
										}
									>
										<td className="row-shift">{unitName}</td>
									</OverlayTrigger>
								)}
								<td className={classNames('row-shift', { 'has-guaranteed': hasGuaranteedSlot })}>
									{shiftStructure.is_orientation && (
										<div className="guaranteed-badge-span orientation">
											<Orientation className="orientation-icon" />
											<span>Orientation</span>
										</div>
									)}
									{newShiftsGuaranteed > 0 && (
										<div className="shift-count-details">
											<span className="guaranteed-badge-span">
												<img className="guaranteed-icon" src={guaranteedIcon} alt="Guaranteed shift" />
												Guaranteed:
											</span>
											<span className="highlighted">{newShiftsGuaranteed}</span>
										</div>
									)}
									{newShiftsNotGuaranteed > 0 && (
										<div className="shift-count-details">
											<span>Not Guaranteed: </span>
											<span className="highlighted">{newShiftsNotGuaranteed}</span>
										</div>
									)}
								</td>
								<td className="row-shift">
									{prevShiftsGuaranteed > 0 && (
										<>
											<div className="shift-count-details">
												<span className="guaranteed-badge-span">
													<img className="guaranteed-icon" src={guaranteedIcon} alt="Guaranteed shift" />
													Guaranteed:
												</span>
												<span className="highlighted">{prevShiftsGuaranteed}</span>
											</div>
											{prevShiftsNotGuaranteed > 0 && <div className="divider" />}
										</>
									)}
									{prevShiftsNotGuaranteed > 0 && (
										<div className="shift-count-details">
											<span>Not Guaranteed: </span>
											<span className="highlighted">{prevShiftsNotGuaranteed}</span>
										</div>
									)}
								</td>
								<td className="row-shift last-column">
									{totalOpenGuaranteed > 0 && (
										<>
											<div className="shift-count-details">
												<span className="guaranteed-badge-span">
													<img className="guaranteed-icon" src={guaranteedIcon} alt="Guaranteed shift" />
													Guaranteed:
												</span>
												<span className="highlighted">{totalOpenGuaranteed}</span>
											</div>
											{totalOpenNotGuaranteed > 0 && <div className="divider" />}
										</>
									)}
									{totalOpenNotGuaranteed > 0 && (
										<div className="shift-count-details">
											<span>Not Guaranteed: </span>
											<span className="highlighted">{totalOpenNotGuaranteed}</span>
										</div>
									)}
								</td>
							</tr>
						);
					})
				)}
			</tbody>
		</table>
	);
};

export default memo(ShiftsConfirmation);
